import { Banner, LoadingSpinner, Typography } from '@neo4j-ndl/react';
import { CheckCircleIconSolid, ExclamationTriangleIconSolid } from '@neo4j-ndl/react/icons';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { useLogger } from '@nx/logger/react';
import {
  PROJECT_BILLING_METHOD,
  TIER,
  consoleApi,
  getApiError,
  getApiErrorMessage,
  useActiveOrg,
  useActiveProject,
  useNotifications,
} from '@nx/state';
import type { Instance, TierConfig } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { Center, Page } from '@nx/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { matchPath, useNavigate } from 'react-router';

import { useTrackUpxEvent } from '../../services/segment/analytics';
import { getValidGdsPluginValue, getValidVectorOptimizedValue } from '../create/form-additional-settings';
import { getVisibleSizes } from '../create/helper';
import { findInstanceSize, getValidSize, isConfigurable } from '../entities/helpers';
import { InstanceCreateFooter } from '../form-components/footer';
import type { InstanceConfigureFormData } from './form-data';
import { constructConfigureTierConfig } from './helpers';
import { NewConfigureInstanceForm } from './new-form';

type ConfigureInstancePageProps = {
  instance: Instance;
  tierConfig: TierConfig;
};

export const NewInstanceConfigurePage = ({ instance, tierConfig }: ConfigureInstancePageProps) => {
  const logger = useLogger();
  const project = useActiveProject();
  const navigate = useNavigate();
  const trackEvent = useTrackUpxEvent();
  const notify = useNotifications();
  const visibleSizes = useMemo(
    () =>
      getVisibleSizes(tierConfig.sizes, {
        version: instance.desiredSettings.version,
        region: instance.region,
        cloudProvider: instance.cloudProvider,
        isTrial: isNotNullish(instance.trialEndTime),
      }),
    [instance, tierConfig],
  );
  const [updateInstanceConfig, updateInstanceConfigRes] = consoleApi.useUpdateInstanceConfigMutation();

  const initialSize = useMemo(() => findInstanceSize(instance, visibleSizes), [instance, visibleSizes]);

  const [data, setData] = useState<InstanceConfigureFormData>(() => {
    const defaults = {
      confirmed: false,
      size: undefined,
      vectorOptimized: instance.vectorOptimized,
      gdsPlugin: instance.gdsPlugin,
      tier: instance.tier,
      version: instance.desiredSettings.version,
      cloudProvider: instance.cloudProvider,
      region: instance.region,
      name: instance.name,
    };

    return {
      ...defaults,
      size: getValidSize(defaults, project, visibleSizes, {
        defaultSize: initialSize,
        resizeThreshold: instance.resizeThreshold,
      }),
    };
  });

  const handleDataChange = (newData: Partial<InstanceConfigureFormData>) => {
    setData((oldData) => ({
      ...oldData,
      ...newData,
      gdsPlugin: getValidGdsPluginValue({ ...oldData, ...newData }),
      vectorOptimized: getValidVectorOptimizedValue({ ...oldData, ...newData }),
    }));
  };

  const dataHasChanged =
    data.size !== initialSize ||
    data.vectorOptimized !== instance.vectorOptimized ||
    data.gdsPlugin !== instance.gdsPlugin ||
    data.name !== instance.name;

  const isPrepaidProject = project.billingMethod === PROJECT_BILLING_METHOD.PREPAID;
  const isPrepaidOnlyOptionSelected = data.size?.isPrepaidOnly;
  const disableResize = !dataHasChanged || (Boolean(isPrepaidOnlyOptionSelected) && !isPrepaidProject);

  const willHaveUnavailability = [TIER.GDS, TIER.AURA_DSE].includes(instance.tier);
  const configurationMessage = willHaveUnavailability
    ? 'Re-configuring your instance will schedule a small amount of unavailability.'
    : 'Read / write availability is maintained during a configuration change.';

  const handleSubmitNewConfiguration = () => {
    if (disableResize) {
      return;
    }

    if (isNotNullish(data.size) && isNotNullish(data.vectorOptimized) && isNotNullish(data.gdsPlugin)) {
      updateInstanceConfig({
        dbId: instance.id,
        memory: data.size.memory,
        storage: data.size.storage,
        cpu: data.size.cpu,
        vectorOptimized: data.vectorOptimized,
        gdsPlugin: data.gdsPlugin,
        name: data.name,
      })
        .unwrap()
        .then(() => {
          trackEvent({
            event: AURA_CONSOLE_EVENTS.INSTANCE_CONFIG_UPDATE,
            scope: APP_SCOPE.aura,
          });
          void navigate(`/projects/${project.id}/instances`);
        })
        .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
          const message = getApiErrorMessage(e);
          logger.error(message);
          notify.addNotification({
            description: message,
            title: 'Error',
            timeout: 5000,
            type: 'danger',
          });
        });
    }
  };
  return (
    <div className="flex h-full flex-col">
      <div className="relative flex w-full grow flex-col overflow-auto">
        <Page contentType="light" className="@container" data-testid="new-configure-instance-page">
          <div className={classNames('flex flex-col gap-2')}>
            <Typography variant="h2" className="flex items-center justify-between">
              Configure instance
            </Typography>
            <div>
              <Typography
                variant="body-small"
                className={classNames('flex items-center gap-1 ', {
                  'text-success-text': !willHaveUnavailability,
                  'text-warning-text': willHaveUnavailability,
                })}
              >
                {willHaveUnavailability ? (
                  <ExclamationTriangleIconSolid className="size-4" />
                ) : (
                  <CheckCircleIconSolid className="size-4" />
                )}
                {configurationMessage}
              </Typography>
            </div>
          </div>
          <NewConfigureInstanceForm
            data={data}
            onChange={handleDataChange}
            instance={instance}
            project={project}
            initialSize={initialSize}
            sizes={visibleSizes}
            tierConfig={tierConfig}
          />
        </Page>
        {/* This is to add padding to the bottom since regular padding didn't work. */}
        <div className="!h-6" />
      </div>
      <InstanceCreateFooter
        confirmText="Configure"
        cancelText="Cancel"
        size={data.size}
        initialSize={initialSize}
        onConfirm={handleSubmitNewConfiguration}
        isLoading={updateInstanceConfigRes.isLoading}
        isDisabled={updateInstanceConfigRes.isLoading || !dataHasChanged}
        onCancel={() => {
          void navigate(`/projects/${project.id}/instances`);
        }}
        project={project}
      />
    </div>
  );
};

export const ConfigureInstancePageGuarded = () => {
  const instanceId = matchPath('/projects/:projectId/instances/configure/:instanceId', location.pathname)?.params
    .instanceId;
  const project = useActiveProject();
  const navigate = useNavigate();
  const org = useActiveOrg();
  const { data: instance, isLoading, isError, error } = consoleApi.useGetInstanceQuery(instanceId ?? skipToken);

  if (!org.featureToggles['plg-simplify-instance-creation']) {
    void navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }

  if (isError || isNullish(instance)) {
    const apiError = getApiError(error);
    return (
      <Page contentType="light">
        <Banner
          description={apiError.message ?? `Instance not found: ${instanceId}`}
          title="Error"
          type="danger"
          htmlAttributes={{ 'data-testid': 'configure-instance-error-banner' }}
        />
      </Page>
    );
  }
  const tierConfig = project.tierConfigs[instance.tier];

  if (!isConfigurable(instance) || isNullish(tierConfig)) {
    return (
      <Page contentType="light">
        <Banner
          title="Error"
          description="The instance is currently not configurable. The instance might be paused, or have an ongoing configuration."
          type="danger"
          htmlAttributes={{ 'data-testid': 'configure-instance-error-banner' }}
        />
      </Page>
    );
  }
  const configureTierConfig = constructConfigureTierConfig(tierConfig, instance);
  return <NewInstanceConfigurePage instance={instance} tierConfig={configureTierConfig} />;
};
